import { db } from '../database/firebase';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { apiGet, apiGetAllPages, useApiData } from './apiClient';

const SALES_COLLECTION = 'sales';

/**
 * @param {{
 *   userID?: string,
 *   eventID?: string,
 *   stockID?: string,
 *   q?: string,
 *   apiFilters?: Record<string, string>,
 *   page?: number,
 *   perPage?: number,
 *   fetchAll?: boolean,
 * }} opts
 */
export const getSales = async ({
  userID,
  eventID,
  stockID,
  q = '',
  apiFilters = {},
  page = 1,
  perPage = 50,
  fetchAll = false,
} = {}) => {
  const byNewest = (rows) =>
    [...(rows || [])].sort((a, b) => Number(b?.createdate || 0) - Number(a?.createdate || 0));

  if (useApiData) {
    const qs = new URLSearchParams();
    qs.set('sort', 'createdate');
    qs.set('order', 'desc');

    if (eventID && !stockID) qs.set('event.id', eventID);
    if (eventID && stockID) {
      qs.set('event.id', eventID);
      qs.set('event.stockID', stockID);
    }
    if (userID) qs.set('user.id', userID);
    if (q?.trim()) qs.set('q', q.trim());

    Object.entries(apiFilters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        qs.set(key, String(value));
      }
    });

    if (fetchAll) {
      return byNewest(await apiGetAllPages('sales', qs));
    }

    qs.set('page', String(page));
    qs.set('perPage', String(Math.min(Math.max(perPage, 1), 100)));
    const res = await apiGet(`sales?${qs.toString()}`);
    return {
      data: byNewest(res?.data ?? []),
      total: res?.total ?? 0,
      page: res?.page ?? page,
      perPage: res?.perPage ?? perPage,
    };
  }

  const collectionRef = collection(db, SALES_COLLECTION);

  let firestoreQ;
  if (eventID && !stockID) {
    firestoreQ = query(
      collectionRef,
      orderBy('createdate', 'desc'),
      where('event.id', '==', eventID),
    );
  } else if (eventID && stockID) {
    firestoreQ = query(
      collectionRef,
      orderBy('createdate', 'desc'),
      where('event.stockID', '==', stockID),
    );
  } else if (userID) {
    firestoreQ = query(
      collectionRef,
      orderBy('createdate', 'desc'),
      where('user.id', '==', userID),
    );
  } else {
    firestoreQ = query(collectionRef, orderBy('createdate', 'desc'), limit(100));
  }

  const snap = await getDocs(firestoreQ);
  const rows = [];
  snap?.forEach((docRef) => {
    const data = docRef.data();
    if (eventID || userID) {
      rows.push({ id: docRef.id, ...data });
    } else if (data.status && data.status !== 0) {
      rows.push({ id: docRef.id, ...data });
    }
  });
  const sorted = byNewest(rows);
  if (fetchAll) return sorted;
  const start = (page - 1) * perPage;
  return {
    data: sorted.slice(start, start + perPage),
    total: sorted.length,
    page,
    perPage,
  };
};

export const getSaleById = async (id) => {
  if (!id) return null;
  if (useApiData) {
    return apiGet(`sales/${id}`);
  }
  const rows = await getSales({ fetchAll: true });
  return rows.find((row) => row.id === id) ?? null;
};

export default {
  getSales,
  getSaleById,
};
